import { generateClient } from 'aws-amplify/api';
import { type GraphQLResult } from '@aws-amplify/api';

const client = generateClient();

// --- Types ---

export interface AgentScheduleRunner {
  id: string;
  scheduleId: string;
  title: string;
  prompt: string;
  attachments?: string | null;
  sortOrder: number;
  sendEmail?: boolean | null;
  emailCondition?: string | null;
  emailConditionMode?: string | null; // "gate" (default when null) | "notify"
  organizationId?: string | null;
  owner?: string | null;
  createdAt: string;
  updatedAt: string;
  conversations?: {
    items: ScheduleConversation[];
  };
}

export interface AgentSchedule {
  id: string;
  title: string;
  description?: string | null;
  runtime: string;
  cronExpression: string;
  timezone: string;
  status: 'ACTIVE' | 'PAUSED' | 'DELETED';
  nextRunAt: string;
  lastRunAt?: string | null;
  runCount: number;
  error?: string | null;
  systemPrompt?: string | null;
  organizationId?: string | null;
  metadata?: string | null;
  projectId?: string | null;
  owner?: string | null;
  createdAt: string;
  updatedAt: string;
  runners?: {
    items: AgentScheduleRunner[];
  };
}

export interface AgentScheduleRun {
  id: string;
  scheduleId: string;
  status: string;
  error?: string | null;
  organizationId?: string | null;
  createdAt: string;
  conversations?: {
    items: ScheduleConversation[];
  };
}

export interface ScheduleConversation {
  id: string;
  title: string;
  model: string;
  modelCodename: string;
  messageCount: number;
  unreadCount: number;
  totalTokens: number;
  lastMessageAt: string;
  isActive: boolean;
  agentScheduleRunnerId?: string | null;
  agentScheduleRunId?: string | null;
  conversationType?: string | null;
  // AWSJSON string; carries conditionResult verdicts for condition-gated scheduled emails.
  metadata?: string | null;
  createdAt: string;
  updatedAt: string;
  messages?: {
    items: ScheduleMessage[];
  };
}

export interface ScheduleMessage {
  id: string;
  conversationId: string;
  content: string;
  isUser: boolean;
  model?: string | null;
  modelCodename?: string | null;
  messageRead?: boolean;
  createdAt: string;
}

// --- Custom GraphQL queries (codegen doesn't nest deeply enough) ---

const LIST_SCHEDULES_WITH_RUNNERS = `
  query ListAgentSchedules($filter: ModelAgentScheduleFilterInput, $limit: Int, $nextToken: String) {
    listAgentSchedules(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        title
        description
        runtime
        cronExpression
        timezone
        status
        nextRunAt
        lastRunAt
        runCount
        error
        systemPrompt
        organizationId
        metadata
        projectId
        createdAt
        updatedAt
        owner
        runners {
          items {
            id
            scheduleId
            title
            prompt
            attachments
            sortOrder
            sendEmail
            emailCondition
            emailConditionMode
            organizationId
            owner
            createdAt
            updatedAt
          }
        }
      }
      nextToken
    }
  }
`;

const GET_SCHEDULE_WITH_RUNNERS = `
  query GetAgentSchedule($id: ID!) {
    getAgentSchedule(id: $id) {
      id
      title
      description
      runtime
      cronExpression
      timezone
      status
      nextRunAt
      lastRunAt
      runCount
      systemPrompt
      organizationId
      metadata
      projectId
      createdAt
      updatedAt
      owner
      runners {
        items {
          id
          scheduleId
          title
          prompt
          attachments
          sortOrder
          sendEmail
          emailCondition
          emailConditionMode
          organizationId
          owner
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const GET_RUNNER_CONVERSATIONS = `
  query MCPConversationsByRunnerId(
    $agentScheduleRunnerId: ID!
    $sortDirection: ModelSortDirection
    $limit: Int
  ) {
    mCPConversationsByAgentScheduleRunnerIdAndCreatedAt(
      agentScheduleRunnerId: $agentScheduleRunnerId
      sortDirection: $sortDirection
      limit: $limit
    ) {
      items {
        id
        title
        model
        modelCodename
        messageCount
        unreadCount
        totalTokens
        lastMessageAt
        isActive
        agentScheduleRunnerId
        conversationType
        createdAt
        updatedAt
        messages {
          items {
            id
            conversationId
            content
            isUser
            model
            modelCodename
            messageRead
            createdAt
          }
        }
      }
    }
  }
`;

const GET_SCHEDULE_RUNS = `
  query AgentScheduleRunsByScheduleId(
    $scheduleId: ID!
    $sortDirection: ModelSortDirection
    $limit: Int
  ) {
    agentScheduleRunsByScheduleIdAndCreatedAt(
      scheduleId: $scheduleId
      sortDirection: $sortDirection
      limit: $limit
    ) {
      items {
        id
        scheduleId
        status
        error
        organizationId
        createdAt
        conversations {
          items {
            id
            title
            model
            modelCodename
            messageCount
            unreadCount
            lastMessageAt
            isActive
            agentScheduleRunnerId
            agentScheduleRunId
            conversationType
            metadata
            createdAt
            messages {
              items {
                id
                conversationId
                content
                isUser
                model
                modelCodename
                messageRead
                createdAt
              }
            }
          }
        }
      }
    }
  }
`;

const CREATE_SCHEDULE = `
  mutation CreateAgentSchedule($input: CreateAgentScheduleInput!) {
    createAgentSchedule(input: $input) {
      id
      title
      description
      runtime
      cronExpression
      timezone
      status
      nextRunAt
      lastRunAt
      runCount
      systemPrompt
      organizationId
      metadata
      createdAt
      updatedAt
      owner
    }
  }
`;

const UPDATE_SCHEDULE = `
  mutation UpdateAgentSchedule($input: UpdateAgentScheduleInput!) {
    updateAgentSchedule(input: $input) {
      id
      title
      description
      runtime
      cronExpression
      timezone
      status
      nextRunAt
      lastRunAt
      runCount
      error
      systemPrompt
      organizationId
      metadata
      createdAt
      updatedAt
      owner
    }
  }
`;

const CREATE_RUNNER = `
  mutation CreateAgentScheduleRunner($input: CreateAgentScheduleRunnerInput!) {
    createAgentScheduleRunner(input: $input) {
      id
      scheduleId
      title
      prompt
      attachments
      sortOrder
      sendEmail
      emailCondition
      emailConditionMode
      organizationId
      owner
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_RUNNER = `
  mutation UpdateAgentScheduleRunner($input: UpdateAgentScheduleRunnerInput!) {
    updateAgentScheduleRunner(input: $input) {
      id
      scheduleId
      title
      prompt
      attachments
      sortOrder
      sendEmail
      emailCondition
      emailConditionMode
      createdAt
      updatedAt
    }
  }
`;

const DELETE_RUNNER = `
  mutation DeleteAgentScheduleRunner($input: DeleteAgentScheduleRunnerInput!) {
    deleteAgentScheduleRunner(input: $input) {
      id
    }
  }
`;

// --- Service ---

export class ScheduleService {
  // throwOnError lets a caller tell "no schedules" apart from "query failed"; default swallows
  static async listSchedules({ throwOnError = false }: { throwOnError?: boolean } = {}): Promise<AgentSchedule[]> {
    try {
      const scheduleMap = new Map<string, AgentSchedule>();
      let nextToken: string | null | undefined = null;

      do {
        const result = await client.graphql({
          query: LIST_SCHEDULES_WITH_RUNNERS,
          variables: {
            filter: { status: { ne: 'DELETED' } },
            limit: 100,
            nextToken,
          },
        }) as GraphQLResult<{ listAgentSchedules: { items: AgentSchedule[]; nextToken?: string | null } }>;

        const items = result.data?.listAgentSchedules?.items || [];
        items.forEach((item) => {
          if (item?.id) {
            scheduleMap.set(item.id, item);
          }
        });

        nextToken = result.data?.listAgentSchedules?.nextToken;
      } while (nextToken);

      const schedules = Array.from(scheduleMap.values())
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      return schedules;
    } catch (error) {
      console.error('[ScheduleService.listSchedules] Error:', error);
      if (throwOnError) throw error;
      return [];
    }
  }

  // Active schedules for a project. Errors propagate (unlike listSchedules) so a project delete never orphans schedules on a listing failure. projectId has no GSI — filters the list, fine at current volumes.
  static async getProjectSchedules(projectId: string): Promise<AgentSchedule[]> {
    const all: AgentSchedule[] = [];
    let nextToken: string | null | undefined = null;

    do {
      const result = await client.graphql({
        query: LIST_SCHEDULES_WITH_RUNNERS,
        variables: {
          filter: { projectId: { eq: projectId }, status: { ne: 'DELETED' } },
          limit: 100,
          nextToken,
        },
      }) as GraphQLResult<{ listAgentSchedules: { items: AgentSchedule[]; nextToken?: string | null } }>;

      all.push(...(result.data?.listAgentSchedules?.items ?? []).filter((s) => s?.id));
      nextToken = result.data?.listAgentSchedules?.nextToken;
    } while (nextToken);

    return all;
  }

  static async getSchedule(id: string): Promise<AgentSchedule | null> {
    try {
      const result = await client.graphql({
        query: GET_SCHEDULE_WITH_RUNNERS,
        variables: { id },
      }) as GraphQLResult<{ getAgentSchedule: AgentSchedule }>;

      return result.data?.getAgentSchedule || null;
    } catch (error) {
      console.error('[ScheduleService.getSchedule] Error:', error);
      return null;
    }
  }

  static async createSchedule(input: {
    id?: string;
    title: string;
    description?: string;
    runtime: string;
    cronExpression: string;
    timezone: string;
    nextRunAt: string;
    systemPrompt?: string;
    projectId?: string;
    metadata?: string;
  }): Promise<AgentSchedule> {
    const result = await client.graphql({
      query: CREATE_SCHEDULE,
      variables: {
        input: {
          ...input,
          status: 'ACTIVE',
          runCount: 0,
        },
      },
    }) as GraphQLResult<{ createAgentSchedule: AgentSchedule }>;

    if (!result.data?.createAgentSchedule) {
      throw new Error('Failed to create schedule');
    }

    return result.data.createAgentSchedule;
  }

  static async updateSchedule(input: {
    id: string;
    title?: string;
    description?: string;
    runtime?: string;
    cronExpression?: string;
    timezone?: string;
    status?: 'ACTIVE' | 'PAUSED' | 'DELETED';
    nextRunAt?: string;
    systemPrompt?: string;
    error?: string | null; // e.g. cleared on Resume after an auto-pause
  }): Promise<AgentSchedule> {
    const result = await client.graphql({
      query: UPDATE_SCHEDULE,
      variables: { input },
    }) as GraphQLResult<{ updateAgentSchedule: AgentSchedule }>;

    if (!result.data?.updateAgentSchedule) {
      throw new Error('Failed to update schedule');
    }

    return result.data.updateAgentSchedule;
  }

  static async deleteSchedule(id: string): Promise<void> {
    await client.graphql({
      query: UPDATE_SCHEDULE,
      variables: {
        input: { id, status: 'DELETED' },
      },
    });
  }

  static async createRunner(input: {
    id?: string;
    scheduleId: string;
    title: string;
    prompt: string;
    attachments?: string;
    sortOrder?: number;
    sendEmail?: boolean;
    emailCondition?: string | null;
    emailConditionMode?: string | null;
  }): Promise<AgentScheduleRunner> {
    const result = await client.graphql({
      query: CREATE_RUNNER,
      variables: {
        input: {
          ...input,
          sortOrder: input.sortOrder ?? 0,
        },
      },
    }) as GraphQLResult<{ createAgentScheduleRunner: AgentScheduleRunner }>;

    if (!result.data?.createAgentScheduleRunner) {
      throw new Error('Failed to create runner');
    }

    return result.data.createAgentScheduleRunner;
  }

  static async updateRunner(input: {
    id: string;
    title?: string;
    prompt?: string;
    attachments?: string;
    sortOrder?: number;
    sendEmail?: boolean;
    emailCondition?: string | null;
    emailConditionMode?: string | null;
  }): Promise<AgentScheduleRunner> {
    const result = await client.graphql({
      query: UPDATE_RUNNER,
      variables: { input },
    }) as GraphQLResult<{ updateAgentScheduleRunner: AgentScheduleRunner }>;

    if (!result.data?.updateAgentScheduleRunner) {
      throw new Error('Failed to update runner');
    }

    return result.data.updateAgentScheduleRunner;
  }

  static async deleteRunner(id: string): Promise<void> {
    await client.graphql({
      query: DELETE_RUNNER,
      variables: { input: { id } },
    });
  }

  static async getRunnerConversations(
    runnerId: string,
    limit: number = 10
  ): Promise<ScheduleConversation[]> {
    try {
      const result = await client.graphql({
        query: GET_RUNNER_CONVERSATIONS,
        variables: {
          agentScheduleRunnerId: runnerId,
          sortDirection: 'DESC',
          limit,
        },
      }) as GraphQLResult<{
        mCPConversationsByAgentScheduleRunnerIdAndCreatedAt: {
          items: ScheduleConversation[];
        };
      }>;

      return result.data?.mCPConversationsByAgentScheduleRunnerIdAndCreatedAt?.items || [];
    } catch (error) {
      console.error('[ScheduleService.getRunnerConversations] Error:', error);
      return [];
    }
  }

  static async getScheduleRuns(
    scheduleId: string,
    limit: number = 20
  ): Promise<AgentScheduleRun[]> {
    try {
      const result = await client.graphql({
        query: GET_SCHEDULE_RUNS,
        variables: {
          scheduleId,
          sortDirection: 'DESC',
          limit,
        },
      }) as GraphQLResult<{
        agentScheduleRunsByScheduleIdAndCreatedAt: {
          items: AgentScheduleRun[];
        };
      }>;

      const runs = result.data?.agentScheduleRunsByScheduleIdAndCreatedAt?.items || [];
      return runs;
    } catch (error) {
      console.error('[ScheduleService.getScheduleRuns] Error:', error);
      return [];
    }
  }
}
